import * as React from "react";
import * as React44ms from "../../../Util/form-utils/react-44ms";

import { InputContainer2023, InputContainer2023Props } from "./input-container";

export interface SingleLineTextInput2023ViewModel<YourNoticeType = any> {
  value: string;
  error?: string;
  warning?: string;
  notice?: YourNoticeType;
  required?: boolean;
  disabled?: boolean;
}

export interface SingleLineTextInput2023Props
  extends Omit<InputContainer2023Props, "name" | "required"> {
  title?: string;
  notice?: string;
  type?: string;
  placeholder?: string;
  className?: string;
  autoComplete?: string;
  charLength?: number;

  formRef: React44ms.Api.TypedRef<SingleLineTextInput2023ViewModel>;
  formLogic?: React.FunctionComponent<{
    formRef: SingleLineTextInput2023Props["formRef"];
  }>;
}

export const SingleLineTextInput2023: React.FunctionComponent<
  SingleLineTextInput2023Props
> = (props) => {
  const [viewModel] = React44ms.React.useForm(props.formRef);
  const { value, error, required, notice, warning, disabled } = viewModel || {};
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const selectionRef = React.useRef<{ start: number; end: number } | null>(
    null
  );

  const name = React.useMemo(() => {
    return React44ms.React.Presentation.elements.nameFromRef(props.formRef);
  }, [props.formRef]);

  if (!name) {
    return null;
  }

  const FormLogic = props.formLogic || (() => null);
  const handleChange: React.ChangeEventHandler<HTMLInputElement> =
      React.useCallback(
          (event) => {
            const input = event.target;

            if (
                input.type !== "number" &&
                input.selectionStart !== null &&
                input.selectionEnd !== null
            ) {
              selectionRef.current = {
                start: input.selectionStart,
                end: input.selectionEnd,
              };
            } else {
              selectionRef.current = null;
            }

            return React44ms.React.Convenient.input.onChange(props.formRef)(event);
          },
          [props.formRef]
      );

  React.useLayoutEffect(() => {
    const input = inputRef.current;
    const selection = selectionRef.current;

    if (!input || !selection) {
      return;
    }

    if (document.activeElement !== input) {
      return;
    }

    if (input.type === "number") {
      return;
    }

    if (
        input.selectionStart === null ||
        input.selectionEnd === null
    ) {
      return;
    }

    const max = input.value.length;
    const start = Math.min(selection.start, max);
    const end = Math.min(selection.end, max);

    try {
      input.setSelectionRange(start, end);
    } catch {
    }
  }, [value]);

  return (
    <React.Fragment>
      <FormLogic formRef={props.formRef} />

      <InputContainer2023
        {...props}
        notice={props.notice || notice}
        errorValueIfAny={error}
        required={required}
        name={name}
        className={`single-line-text-input ${props?.className || ""}`}
      >
        <div className="input-immediate-container">
          <input
            ref={inputRef}
            title={props.title}
            name={name}
            value={value}
            autoComplete={props.autoComplete}
            type={props.type ? props.type : "text"}
            placeholder={props.placeholder}
            onFocus={React44ms.React.Convenient.input.onFocus(props.formRef)}
            onChange={handleChange}
            onBlur={React44ms.React.Convenient.input.onBlur(props.formRef)}
            required={required}
            disabled={!!disabled}
            maxLength={props.charLength}
          />
          {props?.children}
        </div>
      </InputContainer2023>
    </React.Fragment>
  );
};
